import React, { useState } from "react";
import {
    Box,
    Button,
    Container,
    Grid,
    TextField,
    Typography,
    MenuItem,
    IconButton,
    Breadcrumbs,
    Link,
    Divider,
    Autocomplete,
    useTheme,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, ErrorMessage, getIn } from "formik";
import * as Yup from "yup";
import CompulsoryFieldsLabel from "../../helpers/CompulsoryFields"; // Assuming this path is correct
import queryGraphql from "../../helpers/ApiInterface"; // Assuming this path is correct
import { useAuth } from "../../helpers/AuthProvider"; // Assuming this path is correct
import MiniBot from "../MiniBot"; // Assuming this path is correct
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer"; // Adjust path if needed

// AWS Specific configurations
const awsClusterTypeList = ["Auto mode", "Managed node", "Fargate"]; // EKS Cluster Types

const awsRegionList = [
    "us-east-1", "us-east-2", "us-west-1", "us-west-2",
    "af-south-1", "ap-east-1", "ap-south-1", "ap-northeast-1",
    "ap-northeast-2", "ap-northeast-3", "ap-southeast-1", "ap-southeast-2",
    "ap-southeast-3", "ca-central-1", "eu-central-1", "eu-west-1",
    "eu-west-2", "eu-west-3", "eu-north-1", "eu-south-1",
    "me-south-1", "me-central-1", "sa-east-1"
    // Add more regions as needed
];

const validationSchema = Yup.object().shape({
    clusterName: Yup.string()
        .required("This field is required")
        .strict()
        .lowercase("This field must be in lowercase")
        .matches(/^[a-z0-9][a-z0-9-]*$/, "Must start with a lowercase letter or number, and contain only lowercase letters, numbers, or hyphens.")
        .max(100, "This field must not exceed 100 characters"),
    awsAccountId: Yup.string()
        .required("This field is required")
        .matches(/^[0-9]{12}$/, "AWS Account ID must be a 12-digit number"),
    region: Yup.string()
        .required("This field is required")
        .matches(/^[a-z0-9-]+$/, "Region must be alphanumeric with hyphens")
        .strict(),
    gcpClusterViewers: Yup.array() // Kept name gcpClusterViewers for consistency with original structure, but conceptually AWS viewers
        .of(
            Yup.string()
                .required("This field is required")
                .matches(/^[a-zA-Z0-9._%+-]+@searce\.com$/, "Invalid domain (example @searce.com)") // Keep or adjust domain as needed
                .strict()
        )
        .required("This field is required")
        .min(1, "This field is required")
        .strict(),
    applicationRequesters: Yup.array() // Conceptually application owners for AWS
        .of(
            Yup.string()
                .required("This field is required")
                .matches(/^[a-zA-Z0-9._%+-]+@searce\.com$/, "Invalid domain (example @searce.com)") // Keep or adjust domain as needed
                .strict()
        )
        .required("This field is required")
        .min(1, "This field is required")
        .strict(),
    costLabelPairs: Yup.array()
        .of(
            Yup.object({
                cost: Yup.string()
                    .required("This field is required")
                    .strict()
                    .lowercase("This field must be in lowercase")
                    .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
                    .max(20, "This field must not exceed 20 characters"), // Keeping original constraints as per "as it is"
                label: Yup.string()
                    .required("This field is required")
                    .strict()
                    .lowercase("This field must be in lowercase")
                    .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
                    .max(20, "This field must not exceed 20 characters"), // Keeping original constraints
            })
        )
        .required("This field is required"),
    environment: Yup.string().required("This field is required").oneOf(["dev", "staging", "prod"]),
    clusterSize: Yup.string().required("This field is required").oneOf(["small", "medium", "large"]),
    clusterType: Yup.string().required("This field is required").oneOf(awsClusterTypeList),
    departmentValue: Yup.string() // This will be a label for a 'department' tag
        .required("This field is required")
        .strict()
        .lowercase("This field must be in lowercase")
        .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
        .max(30, "This field must not exceed 30 characters"),
    costCenterValue: Yup.string() // This will be a label for a 'cost-center' tag
        .required("This field is required")
        .strict()
        .lowercase("This field must be in lowercase")
        .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
        .max(20, "This field must not exceed 20 characters"),
});


interface AWSClusterFormProps {
    isEdit: boolean;
}

interface AWSClusterFormContentProps {
    isEdit: boolean;
}

interface AwsOneCluster { // Renamed for clarity, update fields based on your actual AWS cluster data model
    id: string;
    clusterName: string;
    environment: string;
    status: string;
    // awsAccountId: string; // Add if this field exists in your list query
    // ... other relevant fields
}

const AWSClusterFormContent: React.FC<AWSClusterFormContentProps> = ({ isEdit }) => {
    const navigate = useNavigate();
    const [users, setUsers] = useState<{ id: string; email: string }[]>([]);
    const { user } = useAuth();
    const isViewer = user.roles?.some((role: string) => role === "viewer") || false;
    const [currentClusterData, setCurrentClusterData] = useState<any>({}); // For storing single cluster data in edit mode
    const [allClustersForEnvCheck, setAllClustersForEnvCheck] = useState<AwsOneCluster[]>([]); // For env disable logic
    const { id } = useParams(); // Cluster ID for edit mode

    const [devDisable, setDevDisable] = useState<boolean>(false);
    const [stageDisable, setStageDisable] = useState<boolean>(true); // Default: only dev enabled
    const [prodDisable, setProdDisable] = useState<boolean>(true);  // Default: only dev enabled

    const [formClusterName, setFormClusterName] = useState<string>(""); // Tracks the cluster name input by user for env checks

    // Fetch single cluster data for editing
    React.useEffect(() => {
        if (!isEdit || !id) return;
        const fetchData = async () => {
            try {
                // IMPORTANT: Update this query to fetch AWS cluster details and use 'awsAccountId'
                const data = await queryGraphql(
                    `
                        query($clusterId: String!){
                          cluster(id:$clusterId) { # Assuming 'cluster' endpoint can fetch AWS clusters
                            id,
                            clusterName,
                            clusterOwner{
                              id,
                              name,
                              googleProfilePicture
                            },
                            awsAccountId, # Changed from gcpProjectId
                            environment,
                            region,
                            clusterSize,
                            clusterType,
                            gcpClusterViewers, # Keep or rename based on backend
                            applicationRequesters, # Keep or rename
                            costLabelPairs{
                              cost,
                              label
                            },
                            status,
                            createdAt
                          }
                        }
                    `,
                    { clusterId: id }
                );
                if (data.cluster) {
                    setCurrentClusterData(data.cluster);
                    setFormClusterName(data.cluster.clusterName || ""); // Initialize for env checks in edit mode
                }
            } catch (error) {
                console.log("Error fetching cluster data:", error);
            }
        };
        fetchData();
    }, [isEdit, id]);

    // Fetch all clusters for environment disable logic
    React.useEffect(() => {
        const fetchAllClustersData = async () => {
            try {
                // IMPORTANT: Update this query if your 'clusters' endpoint or its fields change for AWS
                const data = await queryGraphql(`
                    query Clusters {
                        clusters { # Assuming 'clusters' endpoint fetches AWS clusters too
                            id
                            clusterName
                            environment
                            status
                        }
                    }
                `);
                if (data.clusters) {
                    setAllClustersForEnvCheck(data.clusters.filter((e: any) => e.status !== "rejected"));
                }
            } catch (error) {
                console.log("Error fetching all clusters:", error);
            }
        };
        fetchAllClustersData();
    }, []);

    // Logic to disable environment buttons based on existing clusters
    React.useEffect(() => {
        if (!formClusterName || allClustersForEnvCheck.length === 0) {
            setDevDisable(false);
            setStageDisable(true);
            setProdDisable(true);
            return;
        }

        const devMatch = allClustersForEnvCheck.find(c => c.clusterName === formClusterName && c.environment === "dev" && (!isEdit || c.id !== id));
        const stageMatch = allClustersForEnvCheck.find(c => c.clusterName === formClusterName && c.environment === "staging" && (!isEdit || c.id !== id));
        // const prodMatch = allClustersForEnvCheck.find(c => c.clusterName === formClusterName && c.environment === "prod" && (!isEdit || c.id !== id));
        // The logic implies a sequence for enabling environments
        
        let newDevDisable = false;
        let newStageDisable = true;
        let newProdDisable = true;

        if (devMatch) { // If dev exists for this name
            newDevDisable = true;   // Disable dev
            newStageDisable = false; // Enable staging
            newProdDisable = true;   // Prod remains disabled until staging is chosen/exists
            if (stageMatch) { // If dev and stage exist
                newStageDisable = true; // Disable staging
                newProdDisable = false; // Enable prod
            }
        }
        // If nothing exists, dev is enabled, stage/prod disabled (initial state of newDev/Stage/ProdDisable)

        setDevDisable(newDevDisable);
        setStageDisable(newStageDisable);
        setProdDisable(newProdDisable);

    }, [formClusterName, allClustersForEnvCheck, isEdit, id]);


    // Fetch users for Autocomplete (Cluster Viewers, Application Owners)
    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await queryGraphql(`
                    query {
                        users {
                            id,
                            email
                        }
                    }
                `);
                if (data.users) {
                    setUsers(data.users);
                }
            } catch (error) {
                console.log("Error fetching users:", error);
            }
        };
        fetchData();
    }, []);

    return (
        <Container
            disableGutters
            maxWidth={false}
            sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}
        >
            <MiniBot />
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                    <Link underline="hover" color="inherit" href="/clusters">
                        Clusters
                    </Link>
                    <Typography color="text.primary">
                        {isEdit ? "Edit AWS Cluster Details" : "Add New AWS Cluster"}
                    </Typography>
                </Breadcrumbs>
                <Box display="flex" alignItems="center" mt={2}>
                    <IconButton
                        edge="start"
                        aria-label="back"
                        color="inherit"
                        onClick={() => navigate("/clusters")}
                    >
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography fontFamily="Gilroy-Bold" fontSize="18px">
                        {isEdit ? "Edit AWS Cluster Details" : "Add New AWS Cluster"}
                    </Typography>
                </Box>
            </Box>
            <Grid container>
                <Grid item xs={1}></Grid>
                <Grid item xs={10}>
                    {((isEdit && currentClusterData.id) || !isEdit) && (
                        <Formik
                            initialValues={{
                                clusterName: isEdit ? currentClusterData.clusterName : "",
                                awsAccountId: isEdit ? currentClusterData.awsAccountId : "", // Changed from gcpProjectId
                                region: isEdit ? currentClusterData.region : "",
                                gcpClusterViewers: isEdit ? currentClusterData.gcpClusterViewers : [],
                                applicationRequesters: isEdit ? currentClusterData.applicationRequesters : [],
                                costLabelPairs: isEdit
                                    ? currentClusterData.costLabelPairs?.filter(
                                        (e: any) => !["department", "cost-center", "cluster-owner"].includes(e.cost)
                                    ) || [] // Ensure it's an array
                                    : [],
                                environment: isEdit ? currentClusterData.environment : "",
                                clusterSize: isEdit ? currentClusterData.clusterSize : "",
                                clusterType: isEdit ? currentClusterData.clusterType : "",
                                departmentValue: isEdit
                                    ? currentClusterData.costLabelPairs?.find((elem: any) => elem.cost === "department")?.label || ""
                                    : "",
                                costCenterValue: isEdit
                                    ? currentClusterData.costLabelPairs?.find((elem: any) => elem.cost === "cost-center")?.label || ""
                                    : "",
                            }}
                            validationSchema={validationSchema}
                            enableReinitialize={true} // Important for edit mode to reinitialize form with fetched data
                            onSubmit={async ({
                                applicationRequesters,
                                clusterType,
                                costLabelPairs,
                                environment,
                                awsAccountId, // Changed from gcpProjectId
                                clusterName,
                                clusterSize,
                                gcpClusterViewers,
                                region,
                                departmentValue,
                                costCenterValue,
                            }, { setSubmitting }) => {
                                let apiKeyValuePairs = [];
                                apiKeyValuePairs.push(
                                    {
                                        cost: "cluster-owner",
                                        label: (user.name as string).toLowerCase().replace(/\s+/g, "-"), // Ensure multiple spaces also become one hyphen
                                    },
                                    {
                                        cost: "department",
                                        label: departmentValue,
                                    },
                                    {
                                        cost: "cost-center",
                                        label: costCenterValue,
                                    }
                                );
                                apiKeyValuePairs.push(
                                    ...costLabelPairs.map((e: any) => {
                                        return { cost: e.cost, label: e.label };
                                    })
                                );

                                const commonInputFields = {
                                    applicationRequesters,
                                    clusterType,
                                    costLabelPairs: apiKeyValuePairs,
                                    environment,
                                    awsAccountId, // Changed from gcpProjectId
                                    clusterName,
                                    clusterSize,
                                    gcpClusterViewers,
                                    region,
                                };

                                try {
                                    if (!isEdit) {
                                        // IMPORTANT: Update mutation name and input type for AWS CreateCluster
                                        const data = await queryGraphql(
                                            `
                                                mutation CreateAwsCluster($input: AwsClusterCreateInput!) { # Update as per your schema
                                                  createAwsCluster(input: $input) { # Update as per your schema
                                                    id
                                                  }
                                                }
                                            `,
                                            { input: commonInputFields }
                                        );

                                        if (data.createAwsCluster?.id) {
                                            // Assuming ResourceStatus is generic
                                            await queryGraphql(`mutation Mutation($input: ResourceStatusCreateInput!) {
                                                createResourceStatus(input: $input) { id }
                                            }`, {
                                                input: {
                                                    currentStatus: "pending",
                                                    resourceEnvironment: environment,
                                                    resourceId: data.createAwsCluster.id,
                                                    resourceName: clusterName,
                                                    resourceType: "cluster" // Or "aws-cluster"
                                                }
                                            });
                                            navigate("/clusters");
                                        }
                                    } else {
                                        // IMPORTANT: Update mutation name and input type for AWS UpdateCluster
                                        const data = await queryGraphql(
                                            `
                                                mutation UpdateAwsCluster($input: AwsClusterUpdateInput!) { # Update as per your schema
                                                  updateAwsCluster(input: $input) { # Update as per your schema
                                                    id
                                                  }
                                                }
                                            `,
                                            { input: { id: currentClusterData.id, ...commonInputFields } }
                                        );
                                        if (data.updateAwsCluster?.id) {
                                             // Assuming ResourceStatus is generic
                                            await queryGraphql(`mutation Mutation($input: ResourceStatusUpdateInput!) {
                                                updateResourceStatus(input: $input) { id }
                                            }`, {
                                                input: {
                                                    currentStatus: "pending", // Or a different status for updates
                                                    resourceId: currentClusterData.id,
                                                    resourceName: clusterName
                                                }
                                            });
                                            navigate("/clusters");
                                        }
                                    }
                                } catch (error) {
                                    console.error("Error submitting form:", error);
                                    // Handle error display to user if necessary
                                } finally {
                                    setSubmitting(false);
                                }
                            }}
                        >
                            {({
                                handleChange,
                                handleBlur,
                                values,
                                setFieldValue,
                                errors,
                                touched,
                                isValid,
                                dirty,
                                isSubmitting,
                            }) => {
                                // Update formClusterName whenever Formik's clusterName changes
                                // React.useEffect(() => {
                                //     if (values.clusterName !== formClusterName) {
                                //         setFormClusterName(values.clusterName);
                                //     }
                                // }, [values.clusterName, formClusterName]);
                                
                                return (
                                    <Form>
                                        <Box>
                                            <Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={4}>
                                                {isEdit
                                                    ? "Make changes to AWS cluster details and save"
                                                    : "Fill the below details to add AWS cluster"}
                                            </Typography>

                                            {/* Basic Information Section */}
                                            <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                                <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
                                                    Basic Information
                                                </Typography>
                                                <Divider sx={{ marginBottom: "24px" }} />
                                                <Grid container spacing={4} style={{ marginBottom: "16px" }}>
                                                    <Grid item xs={12} sm={6}>
                                                        <CompulsoryFieldsLabel fieldName="Cluster Name" />
                                                        <TextField
                                                            sx={{ "& .MuiFilledInput-root": { border: errors["clusterName"] && touched["clusterName"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black", }, }, }}
                                                            variant="filled" fullWidth name="clusterName"
                                                            value={values.clusterName}
                                                            onChange={(e) => {
                                                                handleChange(e);
                                                                setFormClusterName(e.target.value); // Formik handles values.clusterName for effect dependency
                                                            }}
                                                            onBlur={handleBlur}
                                                            placeholder="Enter AWS EKS cluster name"
                                                            size="medium" hiddenLabel
                                                            InputProps={{ disableUnderline: true, style: { borderRadius: "8px" }, }}
                                                        />
                                                        <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                            <ErrorMessage name="clusterName" component="div" />
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <CompulsoryFieldsLabel fieldName="AWS Account ID" />
                                                        <TextField
                                                            variant="filled" fullWidth name="awsAccountId" // Changed from gcpProjectId
                                                            value={values.awsAccountId}
                                                            onChange={handleChange} onBlur={handleBlur}
                                                            placeholder="Enter 12-digit AWS Account ID"
                                                            size="medium"
                                                            sx={{ "& .MuiFilledInput-root": { border: errors["awsAccountId"] && touched["awsAccountId"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black", }, }, }}
                                                            hiddenLabel
                                                            InputProps={{ disableUnderline: true, style: { borderRadius: "8px" }, }}
                                                        />
                                                        <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                            <ErrorMessage name="awsAccountId" component="div" />
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <CompulsoryFieldsLabel fieldName="Cluster Type" />
                                                        <TextField
                                                            select placeholder="Select" name="clusterType"
                                                            value={values.clusterType}
                                                            onChange={handleChange} onBlur={handleBlur}
                                                            variant="filled" fullWidth hiddenLabel size="medium"
                                                            sx={{ "& .MuiFilledInput-root": { border: errors["clusterType"] && touched["clusterType"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black", }, }, }}
                                                            InputProps={{ disableUnderline: true, style: { borderRadius: "8px" }, }}
                                                        >
                                                            {awsClusterTypeList.map((e) => (
                                                                <MenuItem key={e} value={e} onClick={() => setFieldValue("clusterType", e)}>
                                                                    {e}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                        <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                            <ErrorMessage name="clusterType" component="div" />
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <CompulsoryFieldsLabel fieldName="Region" />
                                                        <Autocomplete
                                                            options={awsRegionList} // Use AWS regions
                                                            filterSelectedOptions
                                                            value={values.region || null} // Ensure value is null if empty for Autocomplete
                                                            onChange={(e, newVal) => {
                                                                setFieldValue("region", newVal || ""); // Handle null case from Autocomplete
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    variant="filled" fullWidth placeholder="Select AWS Region"
                                                                    size="medium" hiddenLabel
                                                                    sx={{ "& .MuiFilledInput-root": { border: errors["region"] && touched["region"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black", }, }, }}
                                                                    InputProps={{ ...params.InputProps, style: { borderRadius: "8px" }, disableUnderline: true, }}
                                                                />
                                                            )}
                                                        />
                                                        <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                             <ErrorMessage name="region" component="div" />
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <CompulsoryFieldsLabel fieldName="Environment" />
                                                        <Box display="flex" gap={2} mt="14px">
                                                            {["dev", "staging", "prod"].map(env => (
                                                                <Button
                                                                    key={env}
                                                                    sx={{ borderRadius: 5, borderColor: values.environment === env ? "#000000" : "#666666", color: values.environment === env ? "#000000" : "#666666", bgcolor: values.environment === env ? "#E0E0E0" : "#FFFFFF", textTransform: "none", }}
                                                                    variant="outlined"
                                                                    onClick={() => setFieldValue("environment", env)}
                                                                    disabled={
                                                                        (env === "dev" && devDisable) ||
                                                                        (env === "staging" && stageDisable) ||
                                                                        (env === "prod" && prodDisable)
                                                                    }
                                                                >
                                                                    {env}
                                                                </Button>
                                                            ))}
                                                        </Box>
                                                        <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                            <ErrorMessage name="environment" component="div" />
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <CompulsoryFieldsLabel fieldName="Cluster Size" />
                                                        <Box display="flex" gap={2} mt="14px">
                                                            {["small", "medium", "large"].map(size => (
                                                                <Button
                                                                    key={size}
                                                                    sx={{ borderRadius: 5, borderColor: values.clusterSize === size ? "#000000" : "#666666", color: values.clusterSize === size ? "#000000" : "#666666", bgcolor: values.clusterSize === size ? "#E0E0E0" : "#FFFFFF", textTransform: "none", }}
                                                                    variant="outlined"
                                                                    onClick={() => setFieldValue("clusterSize", size)}
                                                                >
                                                                    {size}
                                                                </Button>
                                                            ))}
                                                        </Box>
                                                        <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                            <ErrorMessage name="clusterSize" component="div" />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                            </div>

                                            {/* Cluster Viewers and Application Owners Section */}
                                            <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                                <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
                                                    Cluster Viewers and Application Owners
                                                </Typography>
                                                <Divider sx={{ marginBottom: "24px" }} />
                                                <Grid container spacing={4} style={{ marginBottom: "16px" }}>
                                                    <Grid item xs={12} sm={6}>
                                                        <CompulsoryFieldsLabel fieldName="Cluster Viewers" />
                                                        <Autocomplete
                                                            multiple options={users.map((e) => e.email)}
                                                            filterSelectedOptions ChipProps={{ size: "small" }} freeSolo
                                                            value={values.gcpClusterViewers} // Keep or rename state based on backend
                                                            onChange={(e, newVal) => { setFieldValue("gcpClusterViewers", newVal); }}
                                                            renderInput={(params) => (
                                                                <TextField {...params} variant="filled" fullWidth placeholder="e.g. user@example.com"
                                                                    size="small" hiddenLabel
                                                                    InputProps={{ ...params.InputProps, style: { borderRadius: "8px" }, disableUnderline: true, }}
                                                                />
                                                            )}
                                                        />
                                                        <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                            {typeof getIn(errors, `gcpClusterViewers`) === "string"
                                                                ? getIn(errors, `gcpClusterViewers`)
                                                                : (getIn(errors, `gcpClusterViewers`) as string[])?.filter(e => e != null)[0]}
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <CompulsoryFieldsLabel fieldName="Application Owners" />
                                                        <Autocomplete
                                                            multiple options={users.map((e) => e.email)}
                                                            filterSelectedOptions ChipProps={{ size: "small" }} freeSolo
                                                            value={values.applicationRequesters}
                                                            onChange={(e, newVal) => { setFieldValue("applicationRequesters", newVal); }}
                                                            renderInput={(params) => (
                                                                <TextField {...params} variant="filled" fullWidth placeholder="e.g. owner@example.com"
                                                                    size="small" hiddenLabel
                                                                    InputProps={{ ...params.InputProps, style: { borderRadius: "8px" }, disableUnderline: true, }}
                                                                />
                                                            )}
                                                        />
                                                        <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                            {typeof getIn(errors, `applicationRequesters`) === "string"
                                                                ? getIn(errors, `applicationRequesters`)
                                                                : (getIn(errors, `applicationRequesters`) as string[])?.filter(e => e != null)[0]}
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                            </div>

                                            {/* Cluster Tags / Labels Section */}
                                            <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                                <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}> Cluster Tags / Labels </Typography>
                                                <Divider sx={{ marginBottom: "24px" }} />
                                                <Grid container spacing={4} style={{ marginBottom: "16px" }}>
                                                    <Grid item xs={12} sm={6}><Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px"> Key </Typography> </Grid>
                                                    <Grid item xs={12} sm={6}><Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px"> Value </Typography> </Grid>
                                                </Grid>
                                                <Grid container spacing={4}>
                                                    {/* Cluster Owner Tag (Read-only Key) */}
                                                    <Grid item xs={12} sm={6}> <TextField fullWidth placeholder="Enter key" size="medium" hiddenLabel disabled value="cluster-owner" variant="filled" sx={{ marginBottom: "0px" }} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" }, }} /> </Grid>
                                                    <Grid item xs={12} sm={6}> <Box display="flex" alignItems="center" justifyContent="space-between"> <TextField fullWidth placeholder="Enter value" size="medium" value={(isEdit && currentClusterData.clusterOwner ? (currentClusterData.clusterOwner.name as string) : (user.name as string) ).toLowerCase().replace(/\s+/g, "-")} hiddenLabel sx={{ marginBottom: "0px" }} variant="filled" disabled InputProps={{ disableUnderline: true, style: { borderRadius: "8px" }, }} /> </Box> </Grid>
                                                    {/* Department Tag */}
                                                    <Grid item xs={12} sm={6}> <TextField fullWidth placeholder="Enter key" size="medium" hiddenLabel disabled value="department" variant="filled" sx={{ marginBottom: "0px" }} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" }, }} /> </Grid>
                                                    <Grid item xs={12} sm={6}> <Box display="flex" alignItems="center" justifyContent="space-between"> <TextField fullWidth placeholder="Enter department value" size="medium" value={values.departmentValue} hiddenLabel variant="filled" name="departmentValue" sx={{ "& .MuiFilledInput-root": { border: errors["departmentValue"] && touched["departmentValue"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black", }, }, marginBottom: "0px", }} onChange={handleChange} onBlur={handleBlur} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" }, }} /> </Box> <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}> <ErrorMessage name="departmentValue" component="div" /> </div> </Grid>
                                                    {/* Cost Center Tag */}
                                                    <Grid item xs={12} sm={6}> <TextField fullWidth placeholder="Enter key" size="medium" hiddenLabel disabled value="cost-center" variant="filled" sx={{ marginBottom: "0px" }} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" }, }} /> </Grid>
                                                    <Grid item xs={12} sm={6}> <Box display="flex" alignItems="center" justifyContent="space-between"> <TextField fullWidth placeholder="Enter cost center value" size="medium" value={values.costCenterValue} hiddenLabel variant="filled" name="costCenterValue" sx={{ "& .MuiFilledInput-root": { border: errors["costCenterValue"] && touched["costCenterValue"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black", }, }, marginBottom: "0px", }} onChange={handleChange} onBlur={handleBlur} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" }, }} /> </Box> <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}> <ErrorMessage name="costCenterValue" component="div" /> </div> </Grid>
                                                </Grid>
                                                {/* Dynamically Added Tags */}
                                                <Grid container spacing={4} mt="8px">
                                                    {values.costLabelPairs.map((pair: any, index: number) => (
                                                        <React.Fragment key={pair.id || index}>
                                                            <Grid item xs={12} sm={6}>
                                                                <TextField fullWidth placeholder="Enter key" size="medium" hiddenLabel value={pair.cost}
                                                                    sx={{ "& .MuiFilledInput-root": { border: getIn(errors, `costLabelPairs[${index}].cost`) && getIn(touched, `costLabelPairs[${index}].cost`) ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black", }, }, marginBottom: "0px", }}
                                                                    variant="filled"
                                                                    onChange={(event) => {
                                                                        setFieldValue(`costLabelPairs[${index}].cost`, event.target.value || "");
                                                                    }}
                                                                    InputProps={{ disableUnderline: true, style: { borderRadius: "8px" }, }}
                                                                />
                                                                <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}> {getIn(errors, `costLabelPairs[${index}].cost`)} </div>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Box display="flex" alignItems="center" justifyContent="space-between" >
                                                                    <TextField fullWidth placeholder="Enter value" size="medium"
                                                                        sx={{ "& .MuiFilledInput-root": { border: getIn(errors, `costLabelPairs[${index}].label`) && getIn(touched, `costLabelPairs[${index}].label`) ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black", }, }, marginBottom: "0px", }}
                                                                        value={pair.label} hiddenLabel variant="filled"
                                                                        onChange={(event) => {
                                                                            setFieldValue(`costLabelPairs[${index}].label`, event.target.value || "");
                                                                        }}
                                                                        InputProps={{ disableUnderline: true, style: { borderRadius: "8px" }, }}
                                                                    />
                                                                    <Button sx={{ marginLeft: "8px" }} onClick={() => { const clpArr = values.costLabelPairs.filter((p: any, i: number) => i !== index); setFieldValue("costLabelPairs", clpArr); }} > <img alt="close-icon" src="/assets/close-icon.svg" /> </Button>
                                                                </Box>
                                                                <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}> {getIn(errors, `costLabelPairs[${index}].label`)} </div>
                                                            </Grid>
                                                        </React.Fragment>
                                                    ))}
                                                </Grid>
                                                <Button variant="text" sx={{ color: "#000000", textTransform: "none", mt: "16px" }} onClick={() => setFieldValue("costLabelPairs", [...values.costLabelPairs, { cost: "", label: "", id: crypto.randomUUID() }, ])} > + Add Key Value Pair </Button>
                                            </div>
                                        </Box>

                                        <Box display="flex" justifyContent="flex-end" gap={2}>
                                            <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4, }} variant="outlined" size="large" onClick={() => navigate("/clusters")} > Cancel </Button>
                                            <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", "&:hover": { backgroundColor: "white", color: "black", borderColor: "black", }, textTransform: "none", px: 4, }} variant="contained" size="large" type="submit" disabled={!(isValid && dirty) || isViewer || isSubmitting} >
                                                {isSubmitting ? (isEdit ? "Saving..." : "Adding...") : (isEdit ? "Save Changes" : "Add New AWS Cluster") }
                                            </Button>
                                        </Box>
                                    </Form>
                                );
                            }}
                        </Formik>
                    )}
                </Grid>
                <Grid item xs={1}></Grid>
            </Grid>
        </Container>
    );
};

const AWSClusterForm: React.FC<AWSClusterFormProps> = ({ isEdit }) => {
    const theme = useTheme();
    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);
    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
    };
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer
                index={"clusters"}
                onStateChange={handleDrawerStateChange}
            />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 0,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    marginLeft: `${currentMarginLeft}px`, // Ensure units are consistent, original used vh, this uses px like MainDrawer
                }}
            >
                <AWSClusterFormContent isEdit={isEdit} />
            </Box>
        </Box>
    );
};

export default AWSClusterForm;